import React, { useState, useMemo } from 'react';
import { CSVLink } from 'react-csv';
import * as XLSX from 'xlsx';
import { useI18n } from '../../i18n/i18nService';
import { InputField, ThemedCheckbox } from './Form';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const DataTable = ({
  data = [],
  columns = [],
  title = "Tableau de données",
  children, // Pour les boutons d'action
  defaultView = 'table',
  onSort,
  onRowSelect,
  initialSort = { key: null, direction: 'asc' },
  isLoading = false,
  customGraphView // Nouvelle prop pour la vue graphique
}) => {
  // États internes
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState([]);
  const [viewMode, setViewMode] = useState(defaultView);
  const [sortConfig, setSortConfig] = useState(initialSort);
  const { t } = useI18n(['datatable', 'common']);
  // Données filtrées
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    return data.filter(row =>
      columns.some(col => {
        const value = row[col.key];
        return String(value).toLowerCase().includes(searchTerm.toLowerCase());
      })
    );
  }, [data, searchTerm, columns]);

  // Données triées
  const sortedData = useMemo(() => {
    if (onSort) return filteredData;

    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig, onSort]);

  // Pagination
  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Gestion du tri
  const handleSort = (key) => {
    if (onSort) {
      onSort(key);
      return;
    }

    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Gestion de la sélection
  const handleSelectRow = (id) => {
    const newSelection = selectedRows.includes(id)
      ? selectedRows.filter(rowId => rowId !== id)
      : [...selectedRows, id];

    setSelectedRows(newSelection);
    if (onRowSelect) onRowSelect(newSelection);
  };

  const handleSelectAll = (e) => {
    const newSelection = e.target.checked ? paginatedData.map(row => row.id) : [];
    setSelectedRows(newSelection);
    if (onRowSelect) onRowSelect(newSelection);
  };

  // Export des données
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(sortedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Export");
    XLSX.writeFile(wb, `${title}_export.xlsx`);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white">
          {title}
          {isLoading && <span className="ml-2 text-sm text-gray-500">{t('loading')}...</span>}
        </h2>

        <div className="flex flex-wrap gap-2">
          {children}

          {customGraphView && (
            <div className="flex border rounded-md overflow-hidden">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 text-sm ${viewMode === 'table' ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-gray-700'}`}
              >
                {t('datatable:table')}
              </button>
              <button
                onClick={() => setViewMode('chart')}
                className={`px-3 py-1.5 text-sm ${viewMode === 'chart' ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-gray-700'}`}
              >
                {t('datatable:graphic')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Barre de contrôle */}
      <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <InputField
            type="text"
            placeholder={t('datatable:search_placeholder')}
            value={searchTerm}
            className="w-full pl-10 pr-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            icon={<MagnifyingGlassIcon className="h-5 w-5 text-light-text dark:text-dark-text" />}
          />
        </div>

        <div className="flex gap-2">
          <CSVLink
            data={sortedData}
            filename={`${title}.csv`}
            className="px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md flex items-center gap-1"
          >
            <span>{t('datatable:csv')}</span>
          </CSVLink>

          <button
            onClick={exportToExcel}
            className="px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md flex items-center gap-1"
          >
            <span>{t('datatable:excel')}</span>
          </button>
        </div>
      </div>

      {/* Contenu principal */}
      {viewMode === 'table' || !customGraphView ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-10">
                  <ThemedCheckbox
                    checked={selectedRows.length > 0 && selectedRows.length === paginatedData.length}
                    onChange={handleSelectAll}
                  />
                </th>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort(column.key)}
                  >
                    <div className="flex items-center">
                      {column.headerName || column.label}
                      {sortConfig.key === column.key && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length + 1} className="px-4 py-6 text-center">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="animate-pulse mt-4 flex flex-row items-center justify-between space-x-4 w-full">
                        <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-4 w-1/4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                    ))}
                  </td>
                </tr>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((row) => (
                  <tr
                    key={row.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedRows.includes(row.id) ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <ThemedCheckbox
                        checked={selectedRows.includes(row.id)}
                        onChange={() => handleSelectRow(row.id)}
                      />
                      {/* <input
                        type="checkbox"
                        checked={selectedRows.includes(row.id)}
                        onChange={() => handleSelectRow(row.id)}
                      /> */}
                    </td>
                    {columns.map((column) => (
                      <td
                        key={`${row.id}-${column.key}`}
                        className={`px-4 py-3 tex-sm text-light-text dark:text-dark-text whitespace-nowrap ${column.className || ''}`}
                      >
                        {column.render ? column.render(row) : row[column.key]}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length + 1} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                    {t('datatable:no_data_available')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mt-6">
          {customGraphView(sortedData)}
        </div>
      )}

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {totalItems} {t('datatable:items')} • {t('datatable:page')} {currentPage} {t('datatable:of')} {totalPages}
        </div>

        <div className="flex items-center gap-2">
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border rounded-md px-2 py-1 text-sm dark:bg-gray-700"
          >
            {[5, 10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} / {t('datatable:page')}
              </option>
            ))}
          </select>

          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1 || isLoading}
            className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 dark:bg-gray-700"
          >
            {t('datatable:previous')}
          </button>

          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages || isLoading}
            className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 dark:bg-gray-700"
          >
            {t('datatable:next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataTable;